import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Users,
  Wallet,
  Share2,
  ArrowRight,
  Copy,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
} from "lucide-react";
import { AuthContext } from "@/contexts/authContext";
import { useCoreController } from "@/services/coreController";
import { affiliateService } from "@/services/affiliateService";
import { ReloadModel, isValidPrefix } from "@/constants";
import { toast } from "sonner";

type Tab = "referrals" | "deposits";

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, setUser, isLoading: userLoading } = useContext(AuthContext);
  const coreController = useCoreController();

  const [stats, setStats] = useState({
    balance: 0,
    referralCount: 0,
    loading: true,
  });
  const [activeTab, setActiveTab] = useState<Tab>("referrals");

  // Filleuls
  const [referrals, setReferrals] = useState<{ client_id: string; date_created: string }[]>([]);
  const [referralsPage, setReferralsPage] = useState(1);
  const [referralsTotal, setReferralsTotal] = useState(0);
  const [referralsLoading, setReferralsLoading] = useState(false);

  // Dépôts des filleuls
  const [deposits, setDeposits] = useState<ReloadModel[]>([]);
  const [depositsPage, setDepositsPage] = useState(1);
  const [depositsTotal, setDepositsTotal] = useState(0);
  const [depositsLoading, setDepositsLoading] = useState(false);

  // Création automatique de la relation commerciale si elle manque
  const [relationPhone, setRelationPhone] = useState("");
  const [creatingRelation, setCreatingRelation] = useState(false);
  const [relationError, setRelationError] = useState<string | null>(null);
  const autoRelationTried = useRef(false);

  const ITEMS_PER_PAGE = 10;
  const themeColor = getAccountColor(coreController.compte.type);
  // Use the current browser domain and the phone number as the commercial code.
  const baseReferralDomain =
    typeof window !== "undefined"
      ? `${window.location.protocol}//${window.location.host}`
      : "https://spice-service.web.app";
  const referralLink = user?.phone_number
    ? `${baseReferralDomain}/share/${user.phone_number}`
    : "";

  const createRelation = async (phoneNumber: string) => {
    if (!user?.id) return;
    setCreatingRelation(true);
    setRelationError(null);
    const result = await affiliateService.createCommercialRelation({
      userId: user.id,
      phoneNumber,
      nom: user.last_name,
      prenom: user.first_name,
    });
    setCreatingRelation(false);
    if (!result.success) {
      setRelationError(result.error ?? "Impossible de créer votre espace parrain");
      return;
    }
    setUser?.((u) => (u ? { ...u, phone_number: phoneNumber, balance: result.balance } : u));
    toast.success("Votre lien de parrainage est activé");
  };

  // Pas de commercial_relations : on la crée avec le numéro saisi à
  // l'inscription s'il est connu, sinon on le demande (voir le formulaire).
  useEffect(() => {
    if (userLoading || !user?.id || user.phone_number || autoRelationTried.current) return;
    autoRelationTried.current = true;
    let storedPhone: string | undefined;
    try {
      const stored = JSON.parse(localStorage.getItem("userData") || "{}");
      if (stored?.id === user.id) storedPhone = stored.phone_number || stored.phoneNumber;
    } catch {
      storedPhone = undefined;
    }
    if (storedPhone && /^226\d{8}$/.test(storedPhone)) {
      createRelation(storedPhone);
    }
  }, [userLoading, user?.id, user?.phone_number]);

  const handleCreateRelation = () => {
    const digits = relationPhone.replace(/\D/g, "");
    if (digits.length !== 8 || !isValidPrefix(digits)) {
      setRelationError("Entrez un numéro valide à 8 chiffres (70, 74, ...)");
      return;
    }
    createRelation(`226${digits}`);
  };

  useEffect(() => {
    const load = async () => {
      if (!user?.id || !user?.phone_number) {
        setStats((s) => ({ ...s, loading: false }));
        return;
      }
      const [balanceResult, referralsResult] = await Promise.all([
        affiliateService.fetchAffiliateBalance(user.id),
        affiliateService.fetchReferrals(user.phone_number),
      ]);
      if (balanceResult.success) {
        setStats({
          balance: balanceResult.balance ?? 0,
          referralCount: balanceResult.referralCount ?? 0,
          loading: false,
        });
      } else {
        setStats((s) => ({ ...s, loading: false }));
      }
      if (referralsResult.success) {
        setReferrals(referralsResult.referrals ?? []);
        setReferralsTotal(referralsResult.referrals?.length ?? 0);
      }
    };
    load();
  }, [user?.id, user?.phone_number]);

  useEffect(() => {
    const loadDeposits = async () => {
      if (!user?.phone_number) return;
      setDepositsLoading(true);
      const result = await affiliateService.fetchReferralDeposits(
        user.phone_number,
        depositsPage,
        ITEMS_PER_PAGE
      );
      setDepositsLoading(false);
      if (result.success) {
        setDeposits(result.deposits ?? []);
        setDepositsTotal(result.total ?? 0);
      }
    };
    loadDeposits();
  }, [user?.phone_number, depositsPage]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(referralLink);
    toast.success("Lien de parrainage copié");
  };

  const paginatedReferrals = referrals.slice(
    (referralsPage - 1) * ITEMS_PER_PAGE,
    referralsPage * ITEMS_PER_PAGE
  );
  const referralsPageCount = Math.ceil(referralsTotal / ITEMS_PER_PAGE);
  const depositsPageCount = Math.ceil(depositsTotal / ITEMS_PER_PAGE);

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-md mx-auto px-4 py-4 flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors mr-2"
            style={{ color: themeColor }}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="flex-1 text-lg font-semibold text-gray-900">Espace parrain</h1>
          <div className="text-right">
            <p className="text-xs text-gray-500">Solde commissions</p>
            <p className="font-bold" style={{ color: themeColor }}>
              {stats.loading ? "..." : `${stats.balance.toLocaleString("fr-FR")} FCFA`}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-5 space-y-5">
        {/* Carte parrainage */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl p-5 border bg-white shadow-sm"
        >

          <div className="grid grid-cols-2 gap-3 mb-4 mt-4">
            <div className="rounded-xl p-3 border bg-gray-50">
              <div className="flex items-center gap-2 mb-1">
                <Users className="w-4 h-4" style={{ color: themeColor }} />
                <span className="text-xs text-gray-500">Filleuls</span>
              </div>
              <div className="text-xl font-bold text-gray-900">
                {stats.loading ? <Loader2 className="w-4 h-4 animate-spin" /> : stats.referralCount}
              </div>
            </div>
            <div className="rounded-xl p-3 border bg-gray-50">
              <div className="flex items-center gap-2 mb-1">
                <Wallet className="w-4 h-4" style={{ color: themeColor }} />
                <span className="text-xs text-gray-500">Commissions</span>
              </div>
              <div className="text-xl font-bold text-green-600">
                {stats.loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  `${stats.balance.toLocaleString("fr-FR")} FCFA`
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 my-4">
            <div className="p-2 rounded-full" style={{ background: `${themeColor}1A` }}>
              <Share2 className="w-5 h-5" style={{ color: themeColor }} />
            </div>
            <h2 className="font-semibold text-gray-800">Votre lien de parrainage</h2>
          </div>
          <div className="space-y-2">
            {referralLink ? (
              <>
                <input
                  value={referralLink}
                  readOnly
                  className="w-full text-xs bg-gray-50 border rounded-lg px-3 py-2 break-all"
                />
                <button
                  onClick={handleCopyLink}
                  className="w-full p-2 rounded-lg bg-white border shadow-sm hover:shadow-md transition-shadow flex items-center justify-center gap-2"
                  style={{ color: themeColor }}
                >
                  <Copy className="w-4 h-4" />
                  <span className="text-sm font-medium">Copier le lien</span>
                </button>
              </>
            ) : creatingRelation ? (
              <div className="flex items-center justify-center gap-2 py-3 text-sm text-gray-600">
                <Loader2 className="w-4 h-4 animate-spin" />
                Activation de votre lien...
              </div>
            ) : (
              <>
                <p className="text-xs text-gray-600">
                  Entrez votre numéro de téléphone pour activer votre lien de parrainage.
                </p>
                <div className="flex items-center border rounded-lg bg-gray-50 overflow-hidden">
                  <span className="px-3 py-2 text-base text-gray-500 border-r">+226</span>
                  <input
                    value={relationPhone}
                    onChange={(e) => setRelationPhone(e.target.value.replace(/\D/g, "").slice(0, 8))}
                    inputMode="numeric"
                    placeholder="70000000"
                    className="flex-1 bg-transparent px-3 py-2 text-base outline-none"
                  />
                </div>
                {relationError && <p className="text-xs text-red-600">{relationError}</p>}
                <button
                  onClick={handleCreateRelation}
                  className="w-full p-2 rounded-lg text-white text-sm font-medium"
                  style={{ background: themeColor }}
                >
                  Activer mon lien
                </button>
              </>
            )}
          </div>
        </motion.div>

        {/* Retrait */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="rounded-2xl p-5 border bg-white shadow-sm"
        >
          <div className="flex items-start gap-3 mb-4">
            <Wallet className="w-6 h-6 shrink-0 mt-0.5" style={{ color: themeColor }} />
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-800">
                Montant disponible au retrait : {" "}
                <span className="font-bold text-green-700">{stats.balance.toLocaleString("fr-FR")} FCFA</span>
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate("/affiliate-withdraw")}
            disabled={stats.balance < 100}
            className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-green-600 text-white font-medium shadow-md hover:shadow-lg transition-shadow disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            Retirer mes commissions
            <ArrowRight className="w-4 h-4" />
          </button>
        </motion.div>

        {/* Onglets listes */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl border shadow-sm overflow-hidden"
        >
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab("referrals")}
              className={`flex-1 py-3 text-sm font-medium ${
                activeTab === "referrals"
                  ? "text-green-600 border-b-2 border-green-600"
                  : "text-gray-500"
              }`}
            >
              Filleuls ({referralsTotal})
            </button>
            <button
              onClick={() => setActiveTab("deposits")}
              className={`flex-1 py-3 text-sm font-medium ${
                activeTab === "deposits"
                  ? "text-green-600 border-b-2 border-green-600"
                  : "text-gray-500"
              }`}
            >
              Leurs recharges ({depositsTotal})
            </button>
          </div>

          <div className="p-4">
            {activeTab === "referrals" && (
              <ReferralsList
                items={paginatedReferrals}
                loading={referralsLoading}
                page={referralsPage}
                pageCount={referralsPageCount}
                onPageChange={setReferralsPage}
              />
            )}
            {activeTab === "deposits" && (
              <DepositsList
                items={deposits}
                loading={depositsLoading}
                page={depositsPage}
                pageCount={depositsPageCount}
                onPageChange={setDepositsPage}
              />
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}

function ReferralsList({
  items,
  loading,
  page,
  pageCount,
  onPageChange,
}: {
  items: { client_id: string; date_created: string }[];
  loading: boolean;
  page: number;
  pageCount: number;
  onPageChange: (p: number) => void;
}) {
  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-3">
      {items.length === 0 ? (
        <p className="text-center text-sm text-gray-400 py-8">Aucun filleul pour le moment.</p>
      ) : (
        items.map((r) => (
          <div key={r.client_id} className="flex items-center justify-between p-3 rounded-xl border bg-gray-50">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600">
                {r.client_id.slice(0, 2).toUpperCase()}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">{r.client_id}</p>
                <p className="text-xs text-gray-500">{new Date(r.date_created).toLocaleDateString("fr-FR")}</p>
              </div>
            </div>
          </div>
        ))
      )}
      <Pagination page={page} pageCount={pageCount} onPageChange={onPageChange} />
    </div>
  );
}

function DepositsList({
  items,
  loading,
  page,
  pageCount,
  onPageChange,
}: {
  items: ReloadModel[];
  loading: boolean;
  page: number;
  pageCount: number;
  onPageChange: (p: number) => void;
}) {
  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-3">
      {items.length === 0 ? (
        <p className="text-center text-sm text-gray-400 py-8">Aucun dépôt de filleul.</p>
      ) : (
        items.map((d) => (
          <div key={d.id} className="flex items-center justify-between p-3 rounded-xl border bg-gray-50">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-xs font-bold text-green-700">
                {d.nom?.slice(0, 2).toUpperCase() || "?"}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">{d.nom || d.numero_commercial}</p>
                <p className="text-xs text-gray-500">{new Date(d.date_created).toLocaleString("fr-FR")}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-bold text-gray-900">
                {new Intl.NumberFormat("fr-FR", {
                  style: "currency",
                  currency: "XOF",
                  maximumFractionDigits: 0,
                }).format(d.montant)}
              </p>
            </div>
          </div>
        ))
      )}
      <Pagination page={page} pageCount={pageCount} onPageChange={onPageChange} />
    </div>
  );
}

function Pagination({
  page,
  pageCount,
  onPageChange,
}: {
  page: number;
  pageCount: number;
  onPageChange: (p: number) => void;
}) {
  if (pageCount <= 1) return null;

  return (
    <div className="flex items-center justify-between pt-2">
      <button
        onClick={() => onPageChange(Math.max(1, page - 1))}
        disabled={page <= 1}
        className="flex items-center gap-1 px-3 py-2 rounded-lg border bg-white text-sm disabled:opacity-50"
      >
        <ChevronLeft className="w-4 h-4" /> Précédent
      </button>
      <span className="text-sm text-gray-600">{page} / {pageCount}</span>
      <button
        onClick={() => onPageChange(Math.min(pageCount, page + 1))}
        disabled={page >= pageCount}
        className="flex items-center gap-1 px-3 py-2 rounded-lg border bg-white text-sm disabled:opacity-50"
      >
        Suivant <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}

function getAccountColor(accountType: string | undefined): string {
  switch (accountType?.toUpperCase()) {
    case "1XBET":
      return "#002366";
    case "BETWINNER":
      return "#00C853";
    default:
      return "#FF9800";
  }
}
